import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, CalendarIcon, X } from "lucide-react";
import { ptBR } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { http, formatApiError } from "@/lib/api";
import { APPT_STATUS, formatDateTime, maskConta, maskDoc, maskPhone, onlyDigits } from "@/lib/format";
import { PersonTypeBadge } from "@/components/admin/PersonTypeBadge";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { PageHeader } from "@/components/admin/PageBits";
import { AppointmentStatusSelect } from "@/components/admin/AppointmentPanel";
import { RequestDetailSheet } from "@/components/admin/RequestDetailSheet";

const DateRangeFilter = ({ range, setRange }) => {
  const label = range?.from ? `${range.from.toLocaleDateString("pt-BR")}${range.to ? ` – ${range.to.toLocaleDateString("pt-BR")}` : ""}` : "Período";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex h-10 items-center gap-2 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-slate-300" data-testid="filter-date-picker"><CalendarIcon className="h-4 w-4" /> {label}</button>
      </PopoverTrigger>
      <PopoverContent className="w-auto border-white/10 bg-[#141C2E] p-2 text-slate-100" align="start">
        <Calendar mode="range" locale={ptBR} selected={range} onSelect={setRange} numberOfMonths={1} data-testid="filter-date-calendar" />
      </PopoverContent>
    </Popover>
  );
};

export default function AppointmentsPage() {
  const [rows, setRows] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState(undefined);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (onlyDigits(cnpj)) params.cnpj = onlyDigits(cnpj);
    if (status !== "all") params.status = status;
    if (range?.from) params.date_from = new Date(range.from.setHours(0, 0, 0, 0)).toISOString();
    if (range?.from) params.date_to = new Date(new Date(range.to || range.from).setHours(23, 59, 59, 999)).toISOString();
    try {
      const { data } = await http.get("/admin/appointments", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [cnpj, status, range]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useRealtimeEvent((m) => { if (["appointment.created", "appointment.updated", "request.deleted", "resync"].includes(m.event)) load(); });

  const clear = () => { setCnpj(""); setStatus("all"); setRange(undefined); };

  return (
    <div className="space-y-6">
      <PageHeader title="Agendamentos" subtitle={`${rows.length} pedido(s) encontrados`} />
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-white/[0.06] bg-[#141C2E] p-4" data-testid="appointments-filters">
        <Input value={cnpj} onChange={(e) => setCnpj(maskDoc(e.target.value))} placeholder="Filtrar por CPF ou CNPJ" className="h-10 w-full border-white/10 bg-black/30 font-mono-x text-slate-100 sm:w-60" data-testid="filter-cnpj" />
        <DateRangeFilter range={range} setRange={setRange} />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 w-[200px] border-white/10 bg-black/30 text-slate-100" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#141C2E] text-slate-100">
            <SelectItem value="all">Todos os status</SelectItem>
            {Object.entries(APPT_STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`filter-status-option-${k}`}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={clear} className="btn-ghost-sm" data-testid="filter-clear"><X className="h-3.5 w-3.5" /> Limpar</button>
      </div>
      <div className="overflow-x-auto rounded-md border border-white/[0.06] bg-[#141C2E]">
        <Table data-testid="appointments-table">
          <TableHeader>
            <TableRow className="border-white/[0.06] hover:bg-transparent">
              {["Protocolo", "CPF / CNPJ", "Responsável", "Telefone", "Horário", "Agência", "Conta", "Solicitado em", "Status", ""].map((h) => <TableHead key={h} className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={10} className="py-16 text-center text-slate-500" data-testid="appointments-empty">Nenhum agendamento encontrado.</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.id} className="border-white/[0.04] hover:bg-white/[0.02]" data-testid={`appointment-row-${a.id}`}>
                <TableCell className="font-mono-x text-xs text-slate-300">{a.protocolo}</TableCell>
                <TableCell className="whitespace-nowrap font-mono-x text-sm"><span className="mr-2"><PersonTypeBadge tipo={a.tipo} testId={`appointment-tipo-${a.id}`} /></span>{a.cnpj_fmt}</TableCell>
                <TableCell className="text-sm text-slate-100">{a.nome}</TableCell>
                <TableCell className="whitespace-nowrap font-mono-x text-sm">{maskPhone(a.telefone)}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{a.horario_label}</TableCell>
                <TableCell className="font-mono-x text-sm">{a.agencia}</TableCell>
                <TableCell className="whitespace-nowrap font-mono-x text-sm" data-testid={`appointment-conta-${a.id}`}>{a.conta ? maskConta(a.conta) : "—"}</TableCell>
                <TableCell className="whitespace-nowrap text-sm text-slate-400">{formatDateTime(a.created_at)}</TableCell>
                <TableCell><AppointmentStatusSelect appt={a} onChanged={load} testId={`appointment-status-${a.id}`} /></TableCell>
                <TableCell><button onClick={() => setOpenId(a.request_id)} className="btn-ghost-sm" data-testid={`appointment-open-${a.id}`}><Eye className="h-3.5 w-3.5" /> Detalhes</button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <RequestDetailSheet requestId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
